import { useState } from 'react';
import { MonoLabel, Row, StripePlaceholder } from '@/component';
import { useCurrentUser } from '@/hook/useCurrentUser';
import { ShopLeadForm } from './ShopLeadForm';

const HARDWARE = [
  { kicker: 'Two mics', title: 'In the box', body: 'A pair of wireless mics paired out of the factory. No dongles, no pairing screens.' },
  { kicker: 'One cable', title: 'Any screen', body: 'HDMI to the TV you already own. The display app boots with the system.' },
  { kicker: 'Your library', title: 'Signed in', body: 'Every track you own and every pack in your plan, waiting when you switch it on.' },
];

const MIC_SPECS = [
  ['Range', '30 metres, walls included'],
  ['Battery', '9 hours, charges in the base'],
  ['Latency', 'Under 20 ms to the speakers'],
  ['Per singer', 'Your tuning and your history follow the mic'],
];

/** /shop: the Aeon system and its mics before either ships, with one address
 * field that both "notify me" buttons submit. The field starts as the account
 * email; a tester who wants another address types over it. Packs are not sold
 * here in the beta (#127 adds them). */
export function ShopPage() {
  const { user } = useCurrentUser();
  const [draft, setDraft] = useState<string | null>(null);
  const email = draft ?? user?.email ?? '';

  return (
    <div className="pt-[52px]">
      <MonoLabel tone="down" size="md" className="block">
        Not shipping yet
      </MonoLabel>
      <h1 className="mt-[14px] max-w-[18ch] text-[40px] font-semibold leading-[1.02] tracking-[-0.035em] md:text-[58px] md:leading-none">
        An Aeon system for your room.
      </h1>
      <p className="mt-5 max-w-[52ch] text-[17px] leading-[1.55] text-mute">
        The hardware that runs sessions without a laptop. The mixer, the display app, and a pair of Aeon mics in one
        box. Join the list and you will hear before anyone else.
      </p>
      <ShopLeadForm
        product="system"
        email={email}
        onEmailChange={setDraft}
        cta="Notify me"
        joinedLabel="On the list"
        className="mt-7"
      />
      <StripePlaceholder
        caption="product shot · aeon system, front three-quarter"
        className="mt-12 h-[340px] rounded-lg border border-line p-5"
      />
      <div className="mt-12 grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-11">
        {HARDWARE.map((spec) => (
          <div key={spec.kicker}>
            <MonoLabel className="block">{spec.kicker}</MonoLabel>
            <div className="mt-[10px] text-xl font-semibold tracking-[-0.025em]">{spec.title}</div>
            <p className="mt-2 text-sm leading-[1.6] text-mute">{spec.body}</p>
          </div>
        ))}
      </div>

      <section className="mt-24 border-t border-line pt-[52px]">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] items-center gap-[52px]">
          <div>
            <MonoLabel tone="acc" size="md" className="block">
              Aeon mics
            </MonoLabel>
            <h2 className="mt-[14px] max-w-[20ch] text-[34px] font-semibold leading-[1.04] tracking-[-0.035em] md:text-[44px]">
              Two mics, tuned to the room.
            </h2>
            <p className="mt-[18px] max-w-[46ch] text-[17px] leading-[1.55] text-mute">
              Each mic carries its own singer. The system knows who is holding which one, so your voice gets your
              settings and your history logs to your account.
            </p>
            <div className="mt-8">
              {MIC_SPECS.map(([key, value]) => (
                <Row key={key} className="items-baseline justify-between gap-6">
                  <MonoLabel>{key}</MonoLabel>
                  <span className="max-w-[34ch] text-right text-sm font-medium">{value}</span>
                </Row>
              ))}
            </div>
            <ShopLeadForm
              product="mics"
              email={email}
              cta="Join the mic list"
              joinedLabel="On the mic list"
              note="Sold as a pair, or singly as a spare"
              className="mt-7"
            />
          </div>
          <StripePlaceholder
            caption="product shot · aeon mic pair, upright on stand"
            className="aspect-[4/5] rounded-lg border border-line p-5"
          />
        </div>
      </section>
    </div>
  );
}
